import { useEffect, useState } from "react";
import axios from "axios";
import { GraduationCap, FileText, Send, UserPlus, Trash2, RefreshCw, School, Plus } from "lucide-react";

/**
 * AcademyOpsPanel — Regroupe 3 features académie :
 * 1. Kit Convention pré-rempli (mini-formulaire → PDF signable)
 * 2. Envoi Convention aux Directeurs des 4 écoles
 * 3. Suivi Candidatures Coach & Assistant (CRUD)
 */
export default function AcademyOpsPanel({ API, pin }) {
  const [subTab, setSubTab] = useState("prefill"); // prefill | broadcast | candidatures
  return (
    <div className="bg-white rounded-xl p-5 shadow-lg" data-testid="academy-ops-panel">
      <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-3">
        <GraduationCap className="w-6 h-6 text-emerald-700" />
        Académie · Opérations 2026-2027
      </h4>
      <div className="flex flex-wrap gap-2 border-b mb-4">
        <TabBtn active={subTab === "prefill"} onClick={() => setSubTab("prefill")}
                testId="acad-tab-prefill">
          📝 Convention pré-remplie
        </TabBtn>
        <TabBtn active={subTab === "broadcast"} onClick={() => setSubTab("broadcast")}
                testId="acad-tab-broadcast">
          📤 Envoi aux Directeurs
        </TabBtn>
        <TabBtn active={subTab === "candidatures"} onClick={() => setSubTab("candidatures")}
                testId="acad-tab-candidatures">
          🎯 Suivi Candidatures
        </TabBtn>
      </div>

      {subTab === "prefill" && <ConventionPrefill API={API} pin={pin} />}
      {subTab === "broadcast" && <ConventionBroadcast API={API} pin={pin} />}
      {subTab === "candidatures" && <CandidatureTracker API={API} pin={pin} />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
        active ? "bg-emerald-700 text-white" : "text-gray-600 hover:bg-emerald-50"
      }`}
    >
      {children}
    </button>
  );
}

/* ============ 1. Convention Pré-remplie ============ */
function ConventionPrefill({ API, pin }) {
  const [form, setForm] = useState({
    school_name: "EPP Atchrimi-Komé",
    school_address: "Quartier Atchrimi-Komé, Atakpamé",
    school_type: "publique",
    director_name: "",
    session_day_1: "Lundi",
    session_day_2: "Jeudi",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const generate = async () => {
    if (!form.school_name.trim() || !form.director_name.trim()) {
      setError("Nom de l'école et nom du Directeur sont obligatoires.");
      return;
    }
    try {
      setBusy(true); setError(null); setResult(null);
      const r = await axios.post(`${API}/academy/convention-prefilled`,
        { pin, ...form });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="prefill-form">
      <p className="text-xs text-gray-600">
        Remplissez le nom de l'école, du Directeur et les jours d'entraînement.
        Le PDF sera généré avec ces informations directement insérées dans
        l'identification, l'Article 3 et la zone de signature — prêt à imprimer et signer.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="🏫 Nom de l'école" required>
          <input value={form.school_name}
                 onChange={(e) => upd("school_name", e.target.value)}
                 data-testid="prefill-school-name"
                 className="w-full px-3 py-2 border rounded text-sm" />
        </Field>
        <Field label="👤 Nom du Directeur / de la Directrice" required>
          <input value={form.director_name}
                 onChange={(e) => upd("director_name", e.target.value)}
                 placeholder="M. KOUASSI Kokou / Mme AKOTO Adjoa"
                 data-testid="prefill-director"
                 className="w-full px-3 py-2 border rounded text-sm" />
        </Field>
        <Field label="📮 Adresse de l'école">
          <input value={form.school_address}
                 onChange={(e) => upd("school_address", e.target.value)}
                 data-testid="prefill-address"
                 className="w-full px-3 py-2 border rounded text-sm" />
        </Field>
        <Field label="🏛 Type">
          <select value={form.school_type}
                  onChange={(e) => upd("school_type", e.target.value)}
                  data-testid="prefill-type"
                  className="w-full px-3 py-2 border rounded text-sm">
            <option value="publique">Publique</option>
            <option value="privee_confession">Privée confessionnelle</option>
            <option value="privee_laique">Privée laïque</option>
          </select>
        </Field>
        <Field label="📅 Jour d'entraînement 1">
          <select value={form.session_day_1}
                  onChange={(e) => upd("session_day_1", e.target.value)}
                  data-testid="prefill-day1"
                  className="w-full px-3 py-2 border rounded text-sm">
            {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"].map(d =>
              <option key={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="📅 Jour d'entraînement 2">
          <select value={form.session_day_2}
                  onChange={(e) => upd("session_day_2", e.target.value)}
                  data-testid="prefill-day2"
                  className="w-full px-3 py-2 border rounded text-sm">
            {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"].map(d =>
              <option key={d}>{d}</option>)}
          </select>
        </Field>
      </div>
      <button onClick={generate} disabled={busy}
              data-testid="prefill-generate"
              className={`w-full py-3 rounded-lg font-semibold text-white text-sm ${
                busy ? "bg-gray-400" : "bg-emerald-700 hover:bg-emerald-800"}`}>
        <FileText className="inline w-4 h-4 mr-1" />
        {busy ? "Génération en cours…" : "📄 Générer la Convention pré-remplie"}
      </button>
      {error && <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-sm">❌ {error}</div>}
      {result && (
        <div className="bg-emerald-50 border-2 border-emerald-300 p-4 rounded" data-testid="prefill-result">
          <p className="text-sm text-emerald-900 font-semibold">
            ✅ Convention générée : {result.filename} ({(result.size_bytes/1024).toFixed(1)} Ko)
          </p>
          <a href={result.public_url} target="_blank" rel="noreferrer" download
             data-testid="prefill-download"
             className="mt-2 inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded font-semibold text-sm">
            📥 Télécharger le PDF
          </a>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-700 block mb-1">
        {label}{required && <span className="text-red-600"> *</span>}
      </label>
      {children}
    </div>
  );
}

/* ============ 2. Broadcast to School Directors ============ */
function ConventionBroadcast({ API, pin }) {
  const [schools, setSchools] = useState([
    { school_name: "EPP Atchrimi-Komé", director_name: "", director_email: "" },
    { school_name: "EPP Agnigbanmè", director_name: "", director_email: "" },
    { school_name: "EPP Sada Nord", director_name: "", director_email: "" },
    { school_name: "École St-Kizito", director_name: "", director_email: "" },
  ]);
  const [prefill, setPrefill] = useState(true);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const upd = (i, k, v) => {
    const c = [...schools]; c[i] = { ...c[i], [k]: v }; setSchools(c);
  };

  const send = async () => {
    const validSchools = schools.filter(s =>
      s.school_name && s.director_name && s.director_email && s.director_email.includes("@"));
    if (validSchools.length === 0) {
      alert("Aucune école complète. Renseignez au moins école + directeur + email valide.");
      return;
    }
    if (!window.confirm(
      `Envoyer la convention à ${validSchools.length} école(s) par email ?\n` +
      (prefill ? "Un PDF pré-rempli sera généré pour chaque école." : "PDF générique envoyé.")
    )) return;
    try {
      setBusy(true); setReport(null);
      const r = await axios.post(`${API}/academy/send-convention-to-schools`,
        { pin, prefill, schools: validSchools });
      setReport(r.data);
    } catch (e) {
      alert("❌ Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="broadcast-schools">
      <p className="text-xs text-gray-600">
        Renseignez le Directeur et l'email de chacune des 4 écoles cibles.
        SAFUAYE générera automatiquement une convention pré-remplie pour chacune,
        l'attachera à un email personnalisé et l'enverra via SMTP.
      </p>
      {schools.map((s, i) => (
        <div key={i} className="grid md:grid-cols-3 gap-2 items-end border-b pb-2">
          <Field label={`École ${i+1}`}>
            <input value={s.school_name}
                   onChange={(e) => upd(i, "school_name", e.target.value)}
                   data-testid={`school-${i}-name`}
                   className="w-full px-3 py-2 border rounded text-sm" />
          </Field>
          <Field label="Directeur / Directrice">
            <input value={s.director_name}
                   onChange={(e) => upd(i, "director_name", e.target.value)}
                   placeholder="M. / Mme ..."
                   data-testid={`school-${i}-director`}
                   className="w-full px-3 py-2 border rounded text-sm" />
          </Field>
          <Field label="Email">
            <input type="email" value={s.director_email}
                   onChange={(e) => upd(i, "director_email", e.target.value)}
                   placeholder="directeur@..."
                   data-testid={`school-${i}-email`}
                   className="w-full px-3 py-2 border rounded text-sm" />
          </Field>
        </div>
      ))}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={prefill}
               onChange={(e) => setPrefill(e.target.checked)}
               data-testid="broadcast-prefill-toggle" />
        <span>Pré-remplir la convention avec le nom de chaque école et directeur (recommandé)</span>
      </label>
      <button onClick={send} disabled={busy}
              data-testid="broadcast-send-btn"
              className={`w-full py-3 rounded-lg font-semibold text-white text-sm ${
                busy ? "bg-gray-400" : "bg-purple-700 hover:bg-purple-800"}`}>
        <Send className="inline w-4 h-4 mr-1" />
        {busy ? "Envoi en cours…" : "📤 Envoyer aux Directeurs"}
      </button>
      {report && (
        <div className="bg-slate-50 border rounded p-3 text-sm" data-testid="broadcast-report">
          <p className="font-semibold mb-2">
            📊 Bilan : {report.sent}/{report.total} envoyés · {report.failed} échec(s)
          </p>
          <ul className="space-y-1 text-xs">
            {report.results.map((r, i) => (
              <li key={i} className={r.sent ? "text-emerald-800" : "text-red-800"}>
                {r.sent ? "✅" : "❌"} {r.school} → {r.email || "(sans email)"}
                {" — "}{r.sent ? "envoyé" : r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ============ 3. Candidatures Tracker ============ */
const STATUS_COLORS = {
  "reçue": "bg-slate-100 text-slate-700",
  "entretien": "bg-amber-100 text-amber-800",
  "retenue": "bg-blue-100 text-blue-800",
  "acceptée": "bg-emerald-100 text-emerald-800",
  "rejetée": "bg-red-100 text-red-800",
};

function CandidatureTracker({ API, pin }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    full_name: "", role: "coach", school_preference: "",
    phone: "", email: "", age: "", experience: "",
  });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/academy/candidatures`, { params: { pin } });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!form.full_name.trim()) { alert("Nom complet requis"); return; }
    try {
      await axios.post(`${API}/academy/candidatures`, {
        pin, ...form, age: form.age ? Number(form.age) : null,
      });
      setForm({ full_name: "", role: "coach", school_preference: "",
                phone: "", email: "", age: "", experience: "" });
      setShow(false);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await axios.put(`${API}/academy/candidatures/${id}`, { pin, status, ...extra });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    try {
      await axios.delete(`${API}/academy/candidatures/${id}`, { params: { pin } });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const filtered = filter === "all" ? items :
    filter === "coach" || filter === "assistant" ? items.filter(x => x.role === filter) :
    items.filter(x => x.status === filter);

  const counts = {
    total: items.length,
    coach: items.filter(x => x.role === "coach").length,
    assistant: items.filter(x => x.role === "assistant").length,
    acceptée: items.filter(x => x.status === "acceptée").length,
  };

  return (
    <div data-testid="candidatures-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => setShow(!show)}
                data-testid="cand-add-toggle"
                className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded text-sm font-semibold">
          <Plus className="w-4 h-4" /> {show ? "Fermer" : "Nouvelle candidature"}
        </button>
        <button onClick={load}
                className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 px-3 py-2 rounded text-sm">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </button>
        <div className="flex gap-1 ml-auto flex-wrap text-xs">
          {[
            ["all", `Tous (${counts.total})`],
            ["coach", `Coaches (${counts.coach})`],
            ["assistant", `Assistants (${counts.assistant})`],
            ["entretien", "Entretien"],
            ["acceptée", `Acceptés (${counts.acceptée})`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
                    data-testid={`cand-filter-${k}`}
                    className={`px-2 py-1 rounded ${filter === k
                      ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {show && (
        <div className="bg-emerald-50 border border-emerald-300 p-3 rounded mb-3" data-testid="cand-add-form">
          <div className="grid md:grid-cols-3 gap-2">
            <input placeholder="Nom complet *" value={form.full_name}
                   onChange={(e) => setForm({...form, full_name: e.target.value})}
                   data-testid="cand-full-name"
                   className="px-3 py-2 border rounded text-sm" />
            <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                    data-testid="cand-role"
                    className="px-3 py-2 border rounded text-sm">
              <option value="coach">Coach d'école</option>
              <option value="assistant">Assistant Terrain</option>
            </select>
            <select value={form.school_preference}
                    onChange={(e) => setForm({...form, school_preference: e.target.value})}
                    data-testid="cand-school"
                    className="px-3 py-2 border rounded text-sm">
              <option value="">— École préférée —</option>
              <option>EPP Atchrimi-Komé</option>
              <option>EPP Agnigbanmè</option>
              <option>EPP Sada Nord</option>
              <option>École St-Kizito</option>
            </select>
            <input placeholder="Téléphone / WhatsApp" value={form.phone}
                   onChange={(e) => setForm({...form, phone: e.target.value})}
                   className="px-3 py-2 border rounded text-sm" />
            <input type="email" placeholder="Email" value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}
                   className="px-3 py-2 border rounded text-sm" />
            <input type="number" placeholder="Âge" value={form.age}
                   onChange={(e) => setForm({...form, age: e.target.value})}
                   className="px-3 py-2 border rounded text-sm" />
            <textarea placeholder="Expérience courte" value={form.experience}
                      onChange={(e) => setForm({...form, experience: e.target.value})}
                      className="md:col-span-3 px-3 py-2 border rounded text-sm" rows={2} />
          </div>
          <button onClick={create}
                  data-testid="cand-save"
                  className="mt-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded text-sm font-semibold">
            💾 Enregistrer
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">Aucune candidature.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2 text-left">Nom</th>
                <th className="p-2 text-left">Rôle</th>
                <th className="p-2 text-left">École pref.</th>
                <th className="p-2 text-left">Contact</th>
                <th className="p-2 text-left">Statut</th>
                <th className="p-2 text-left">Entretien</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`cand-row-${c.id}`}>
                  <td className="p-2 font-semibold">{c.full_name}
                    {c.experience && <p className="text-[10px] text-gray-500 italic">{c.experience.slice(0,60)}</p>}
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${c.role === "coach" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>
                      {c.role === "coach" ? "🧢 Coach" : "🎯 Assistant"}
                    </span>
                  </td>
                  <td className="p-2">
                    <School className="inline w-3 h-3 mr-1 text-slate-500" />
                    {c.school_preference || "—"}
                  </td>
                  <td className="p-2">
                    {c.phone && <p>📞 {c.phone}</p>}
                    {c.email && <p className="text-blue-600">✉ {c.email}</p>}
                  </td>
                  <td className="p-2">
                    <select value={c.status}
                            onChange={(e) => updateStatus(c.id, e.target.value)}
                            data-testid={`cand-status-${c.id}`}
                            className={`px-2 py-1 rounded text-[10px] font-semibold border ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>
                      {["reçue", "entretien", "retenue", "acceptée", "rejetée"].map(s =>
                        <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-2">
                    <input type="date" value={c.interview_date || ""}
                           onChange={(e) => updateStatus(c.id, c.status, { interview_date: e.target.value })}
                           className="px-2 py-1 border rounded text-[11px]" />
                  </td>
                  <td className="p-2">
                    <button onClick={() => del(c.id)}
                            data-testid={`cand-delete-${c.id}`}
                            className="text-red-600 hover:bg-red-50 p-1 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
